import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

import type { AuthMeResponse } from '@/api/auth.api';
import { listEmployees } from '@/api/employees.api';
import {
  createCsAgent,
  deleteCsAgent,
  listCsAgents,
  suspendCsAgent,
  unsuspendCsAgent,
  type CsAgentProfile,
} from '@/api/cs/agents.api';
import { CsErrorCard, CsInlineError, CsLoading, SELECT_CLS } from '@/components/cs/PageStates';
import { describeCsError } from '@/components/cs/csError';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { hasCapability } from '@/lib/capabilities';
import { useToast } from '@/hooks/useToast';

/**
 * Operations → Agents (spec §3.10.2). CS agents ARE hub memberships —
 * the profile adds the Chatwoot mapping + CS-only suspension. Create
 * picks from active employees WITH a membership (employees API is the
 * hub's member directory); suspend/unsuspend/delete confirm first.
 * Mutations need manage_cs_agents (client-side visibility only).
 */

export function AgentsTab({ me }: { me: AuthMeResponse }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const canManage = hasCapability(me, 'manage_cs_agents');

  const agentsQuery = useQuery({ queryKey: ['cs', 'agents'], queryFn: listCsAgents });

  const [createOpen, setCreateOpen] = useState(false);
  const [confirming, setConfirming] = useState<{
    profile: CsAgentProfile;
    action: 'suspend' | 'unsuspend' | 'delete';
  } | null>(null);
  const [reason, setReason] = useState('');

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['cs', 'agents'] });
  }

  const actionMutation = useMutation({
    mutationFn: async ({
      profile,
      action,
      reason: actionReason,
    }: {
      profile: CsAgentProfile;
      action: 'suspend' | 'unsuspend' | 'delete';
      reason?: string;
    }) => {
      if (action === 'suspend') {
        await suspendCsAgent(profile.id, actionReason);
      } else if (action === 'unsuspend') {
        await unsuspendCsAgent(profile.id);
      } else {
        await deleteCsAgent(profile.id, actionReason);
      }
    },
    onSuccess: (_data, vars) => {
      invalidate();
      setConfirming(null);
      setReason('');
      toast.show(
        vars.action === 'suspend'
          ? 'Agent suspended from CS.'
          : vars.action === 'unsuspend'
            ? 'Agent unsuspended.'
            : 'CS agent profile removed.',
        { variant: 'success' },
      );
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Agent action failed.'), { variant: 'danger' });
    },
  });

  if (agentsQuery.isLoading) return <CsLoading />;
  if (agentsQuery.error) {
    return <CsErrorCard title="Couldn't load CS agents" error={agentsQuery.error} fallback="Unknown error" />;
  }

  const agents = agentsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-kw-muted">
          {String(agents.length)} CS agent profile(s). Suspension is CS-only — hub login is untouched.
        </p>
        {canManage ? (
          <Button
            size="sm"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus className="size-4" />
            Add agent
          </Button>
        ) : null}
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Email</th>
              <th className="px-4 py-2.5 font-medium">Chatwoot agent id</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              {canManage ? <th className="px-4 py-2.5 font-medium">Actions</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-kw-border">
            {agents.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 5 : 4} className="px-4 py-8 text-center text-[13px] text-kw-muted">
                  No CS agent profiles yet.
                </td>
              </tr>
            ) : (
              agents.map((a) => (
                <tr key={a.id} className="text-[13px] text-kw-shark">
                  <td className="px-4 py-2.5 font-medium">
                    {a.membership.user.firstName} {a.membership.user.lastName}
                  </td>
                  <td className="px-4 py-2.5 text-kw-muted">{a.membership.user.email}</td>
                  <td className="px-4 py-2.5">{a.chatwootAgentId ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    {a.isSuspended ? (
                      <Badge variant="danger">Suspended</Badge>
                    ) : (
                      <Badge variant="success">Active</Badge>
                    )}
                    {a.isSuspended && a.suspensionReason ? (
                      <p className="mt-0.5 text-[11px] text-kw-muted">{a.suspensionReason}</p>
                    ) : null}
                  </td>
                  {canManage ? (
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setConfirming({ profile: a, action: a.isSuspended ? 'unsuspend' : 'suspend' });
                          }}
                        >
                          {a.isSuspended ? 'Unsuspend' : 'Suspend'}
                        </Button>
                        <button
                          type="button"
                          aria-label={`Delete CS profile for ${a.membership.user.firstName} ${a.membership.user.lastName}`}
                          onClick={() => {
                            setConfirming({ profile: a, action: 'delete' });
                          }}
                          className="rounded p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <CreateAgentModal
        open={createOpen}
        existingMembershipIds={agents.map((a) => a.membershipId)}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={() => {
          invalidate();
          toast.show('CS agent profile created.', { variant: 'success' });
        }}
      />

      {/* Confirm suspend/unsuspend/delete */}
      <Modal
        open={confirming !== null}
        onClose={() => {
          setConfirming(null);
          setReason('');
        }}
        title={
          confirming?.action === 'delete'
            ? 'Remove CS agent profile'
            : confirming?.action === 'suspend'
              ? 'Suspend from CS'
              : 'Unsuspend agent'
        }
        size="sm"
      >
        {confirming ? (
          <div className="space-y-4">
            <p className="text-[13px] text-kw-shark">
              {confirming.action === 'delete' ? (
                <>
                  Remove the CS profile for{' '}
                  <strong>
                    {confirming.profile.membership.user.firstName}{' '}
                    {confirming.profile.membership.user.lastName}
                  </strong>
                  ? Their hub membership is untouched.
                </>
              ) : confirming.action === 'suspend' ? (
                <>
                  Suspend{' '}
                  <strong>
                    {confirming.profile.membership.user.firstName}{' '}
                    {confirming.profile.membership.user.lastName}
                  </strong>{' '}
                  from CS actions? They drop out of routing and assignment candidates.
                </>
              ) : (
                <>
                  Restore{' '}
                  <strong>
                    {confirming.profile.membership.user.firstName}{' '}
                    {confirming.profile.membership.user.lastName}
                  </strong>{' '}
                  to CS actions?
                </>
              )}
            </p>
            {confirming.action !== 'unsuspend' ? (
              <Input
                label="Reason (optional, audited)"
                name="cs-agent-reason"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                }}
              />
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setConfirming(null);
                  setReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant={confirming.action === 'delete' ? 'danger' : 'primary'}
                isLoading={actionMutation.isPending}
                onClick={() => {
                  actionMutation.mutate({
                    profile: confirming.profile,
                    action: confirming.action,
                    ...(reason.trim() ? { reason: reason.trim() } : {}),
                  });
                }}
              >
                {confirming.action === 'delete'
                  ? 'Remove profile'
                  : confirming.action === 'suspend'
                    ? 'Suspend'
                    : 'Unsuspend'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Create dialog — membership picker from the employees directory
// ─────────────────────────────────────────────────────────────────────────

function CreateAgentModal({
  open,
  existingMembershipIds,
  onClose,
  onCreated,
}: {
  open: boolean;
  existingMembershipIds: string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [membershipId, setMembershipId] = useState('');
  const [chatwootAgentId, setChatwootAgentId] = useState('');

  const employeesQuery = useQuery({
    queryKey: ['employees', 'active-members'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
    enabled: open,
  });

  const candidates = useMemo(() => {
    const taken = new Set(existingMembershipIds);
    return (employeesQuery.data?.data ?? []).filter(
      (e): e is typeof e & { membershipId: string } => e.membershipId !== null && !taken.has(e.membershipId),
    );
  }, [employeesQuery.data, existingMembershipIds]);

  const mutation = useMutation({
    mutationFn: () =>
      createCsAgent({
        membershipId,
        chatwootAgentId: chatwootAgentId.trim() === '' ? null : Number(chatwootAgentId),
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      setMembershipId('');
      setChatwootAgentId('');
    },
  });

  const chatwootIdInvalid =
    chatwootAgentId.trim() !== '' &&
    (!Number.isInteger(Number(chatwootAgentId)) || Number(chatwootAgentId) <= 0);

  return (
    <Modal open={open} onClose={onClose} title="Add CS agent" size="sm">
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (!membershipId || chatwootIdInvalid) return;
          mutation.mutate();
        }}
        className="space-y-4"
      >
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Member</span>
          <select
            aria-label="Member to make a CS agent"
            value={membershipId}
            onChange={(e) => {
              setMembershipId(e.target.value);
            }}
            className={SELECT_CLS}
            required
          >
            <option value="">Choose a member…</option>
            {candidates.map((e) => (
              <option key={e.membershipId} value={e.membershipId}>
                {e.firstName} {e.lastName} — {e.email}
              </option>
            ))}
          </select>
        </label>
        <Input
          label="Chatwoot agent id (optional)"
          name="cs-chatwoot-agent-id"
          inputMode="numeric"
          value={chatwootAgentId}
          onChange={(e) => {
            setChatwootAgentId(e.target.value);
          }}
          error={chatwootIdInvalid ? 'Must be a positive integer.' : undefined}
          hint="From Chatwoot → Agents. Needed before this agent can be assigned in Chatwoot."
        />
        <CsInlineError error={mutation.error} fallback="Could not create the profile." />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!membershipId || chatwootIdInvalid}>
            Create profile
          </Button>
        </div>
      </form>
    </Modal>
  );
}
